import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MediaServersService } from '../../../api/api-services/media-servers.service';
import { AcceptMediaServerService } from '../../../api/api-services/accept-media-server.service';
import { MediaServersSettingsService } from '../../../api/api-services/media-servers-settings.service';
import { IMediaServersSummaryVm } from '../../../api/media-servers-summary-vm.model';
import { IMediaServersSettingsVm } from '../../../api/media-servers-settings-vm.model';
import { IMediaServerVm } from '../../../api/media-server-vm.model';
import { OperatorVideoRenditionsService } from '../../../api/api-services/operator-video-renditions.service';
import { IOperatorVideoRenditionsVm } from '../../../api/operator-video-renditions-vm.model';

type TagSeverity =
  | 'success'
  | 'secondary'
  | 'info'
  | 'warning'
  | 'danger'
  | 'contrast';

interface ISummaryCard {
  label: string;
  icon: string;
  value: string;
  detail: string;
  highlight?: boolean;
}

@Component({
  selector: 'app-media-servers',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    ProgressSpinnerModule,
    TableModule,
    TagModule,
    TooltipModule,
  ],
  templateUrl: './media-servers.component.html',
  styleUrl: './media-servers.component.scss',
})
export class MediaServersComponent implements OnInit, OnDestroy {
  private _mediaServersService = inject(MediaServersService);
  private _acceptService = inject(AcceptMediaServerService);
  private _settingsService = inject(MediaServersSettingsService);
  private _videoRenditionsService = inject(OperatorVideoRenditionsService);

  loading = false;
  summary: IMediaServersSummaryVm | null = null;
  cards: ISummaryCard[] = [];
  errorMessage: string | null = null;

  settings: IMediaServersSettingsVm | null = null;
  savingSettings = false;

  /** Screen-sized video renditions; null until loaded or when the API does not make them yet. */
  renditions: IOperatorVideoRenditionsVm | null = null;

  /** Guids with an action in flight — keeps the row's buttons disabled until it settles. */
  busyGuids = new Set<string>();

  private _refreshTimer: any = null;
  // A slow response must never overwrite the state of a newer one (same guard as the Serwer tab).
  private _reqSeq = 0;

  ngOnInit(): void {
    this.load();
    this.loadSettings();
    this.loadRenditions();
    // Only the server list and the encoding progress auto-refresh — re-reading the settings on a timer
    // would fight with a checkbox the operator is in the middle of changing.
    this._refreshTimer = setInterval(() => {
      this.load();
      this.loadRenditions();
    }, 15000);
  }

  ngOnDestroy(): void {
    if (this._refreshTimer) clearInterval(this._refreshTimer);
  }

  get awaitingServers(): IMediaServerVm[] {
    return (this.summary?.servers ?? []).filter((x) => !x.isAuthorized);
  }

  get acceptedServers(): IMediaServerVm[] {
    return (this.summary?.servers ?? []).filter((x) => x.isAuthorized);
  }

  refresh(): void {
    this.load();
    this.loadSettings();
    this.loadRenditions();
  }

  /** "1 234 / 4 800 (26%)" — encoded renditions out of all the sources need. */
  get renditionsProgress(): string {
    const r = this.renditions;
    if (!r) return '—';
    if (!r.renditionsExpected) return this.formatNumber(r.renditionsReady);
    const percent = Math.floor((r.renditionsReady / r.renditionsExpected) * 100);
    return `${this.formatNumber(r.renditionsReady)} / ${this.formatNumber(r.renditionsExpected)} (${percent}%)`;
  }

  get renditionsHint(): string {
    const r = this.renditions;
    if (!r) return '';
    if (!r.generateEnabled) {
      return 'Kodowanie wyłączone (VideoRenditions:GenerateEnabled w konfiguracji API) — nowe wersje nie powstają.';
    }
    if (!r.ffmpegAvailable) {
      return 'Brak ffmpeg/ffprobe na serwerze głównym — wersje nie powstają, wszyscy dostają pełne pliki.';
    }
    if (!r.serveEnabled) {
      return 'Wybór wersji wyłączony (VideoRenditions:ServeEnabled) — aplikacje dostają pełne pliki.';
    }
    return 'Aplikacja podaje rozmiar ekranu; dostaje najmniejszą wersję, która go wypełnia, a pełny plik, dopóki takiej nie ma. Kodowane są tylko wysokości mniejsze od oryginału.';
  }

  private loadRenditions(): void {
    this._videoRenditionsService.get().subscribe({
      next: (renditions) => (this.renditions = renditions),
      // An API without renditions answers 404 — the section simply stays hidden.
      error: () => (this.renditions = null),
    });
  }

  onOriginRotationChange(value: boolean): void {
    this.savingSettings = true;
    this._settingsService.put({ originServesInRotation: value }).subscribe({
      next: (settings) => {
        this.settings = settings;
        this.savingSettings = false;
      },
      error: (e) => {
        // Put the checkbox back where it was so it never claims a setting that did not save.
        if (this.settings) this.settings.originServesInRotation = !value;
        this.savingSettings = false;
        this.errorMessage =
          e?.error?.message ?? 'Nie udało się zapisać ustawień.';
      },
    });
  }

  /** Explains the split the current checkbox state produces for the nodes actually serving now. */
  get rotationHint(): string {
    const satellites = this.summary?.availableForTrafficCount ?? 0;

    if (satellites === 0) {
      return 'Brak aktywnych satelitów — cały ruch obsługuje serwer główny niezależnie od tego ustawienia.';
    }

    if (!this.settings?.originServesInRotation) {
      const each = Math.round(100 / satellites);
      return satellites === 1
        ? 'Serwer główny nie wysyła multimediów — całość obsługuje satelita (poza plikami, których jeszcze nie ma w kopii).'
        : `Serwer główny nie wysyła multimediów — ruch dzieli się po ok. ${each}% na każdy z ${satellites} satelitów.`;
    }

    const share = Math.round(100 / (satellites + 1));
    return satellites === 1
      ? `Ruch dzieli się po ok. ${share}% — naprzemiennie serwer główny i satelita.`
      : `Ruch dzieli się po ok. ${share}% na serwer główny i każdy z ${satellites} satelitów.`;
  }

  accept(server: IMediaServerVm): void {
    this.busyGuids.add(server.guid);
    this._acceptService
      .post({ mediaServerGuid: server.guid })
      .subscribe({
        next: () => {
          this.busyGuids.delete(server.guid);
          this.load();
        },
        error: (e) => {
          this.busyGuids.delete(server.guid);
          this.errorMessage =
            e?.error?.message ?? 'Nie udało się zaakceptować serwera.';
        },
      });
  }

  toggleEnabled(server: IMediaServerVm): void {
    this.busyGuids.add(server.guid);
    this._mediaServersService
      .put({ mediaServerGuid: server.guid, enabled: !server.enabled })
      .subscribe({
        next: () => {
          this.busyGuids.delete(server.guid);
          this.load();
        },
        error: (e) => {
          this.busyGuids.delete(server.guid);
          this.errorMessage =
            e?.error?.message ?? 'Nie udało się zmienić stanu serwera.';
        },
      });
  }

  remove(server: IMediaServerVm): void {
    const confirmed = confirm(
      `Usunąć serwer "${server.serverName}"? Jeśli nadal działa, zgłosi się ponownie i będzie wymagał akceptacji.`,
    );
    if (!confirmed) return;

    this.busyGuids.add(server.guid);
    this._mediaServersService.delete(server.guid).subscribe({
      next: () => {
        this.busyGuids.delete(server.guid);
        this.load();
      },
      error: (e) => {
        this.busyGuids.delete(server.guid);
        this.errorMessage = e?.error?.message ?? 'Nie udało się usunąć serwera.';
      },
    });
  }

  isBusy(server: IMediaServerVm): boolean {
    return this.busyGuids.has(server.guid);
  }

  statusLabel(server: IMediaServerVm): string {
    if (!server.isAuthorized) return 'Oczekuje na akceptację';
    if (server.online && !server.isFullySynced) return 'Pobiera pliki';
    if (!server.enabled) return 'Wyłączony';
    if (server.availableForTraffic) return 'Obsługuje ruch';
    if (server.online) return 'Połączony, brak heartbeatu';
    return 'Offline';
  }

  statusSeverity(server: IMediaServerVm): TagSeverity {
    if (!server.isAuthorized) return 'warning';
    if (server.online && !server.isFullySynced) return 'info';
    if (!server.enabled) return 'secondary';
    if (server.availableForTraffic) return 'success';
    if (server.online) return 'warning';
    return 'danger';
  }

  /** Mirrors the API-side gate so the button explains itself instead of failing on click. */
  enableBlocked(server: IMediaServerVm): boolean {
    return !server.enabled && !server.isFullySynced;
  }

  enableBlockedReason(server: IMediaServerVm): string {
    if (!this.enableBlocked(server)) return '';
    if (!server.online) {
      return 'Serwer jest offline — włączenie będzie możliwe, gdy połączy się i pobierze wszystkie pliki.';
    }
    return `Trwa pobieranie kopii plików (${this.mirrorProgress(
      server,
    )}) — włączenie będzie możliwe po zakończeniu synchronizacji.`;
  }

  formatMb(mb?: number): string {
    if (mb === undefined || mb === null) return '—';
    return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
  }

  formatBytes(bytes?: number): string {
    if (bytes === undefined || bytes === null) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024)
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  formatNumber(value?: number): string {
    if (value === undefined || value === null) return '—';
    return value.toLocaleString('pl-PL');
  }

  /** "18 / 20" — how far this node is through mirroring the origin catalog. */
  mirrorProgress(server: IMediaServerVm): string {
    if (!server.catalogFilesTotal) return this.formatNumber(server.filesCount);
    return `${this.formatNumber(server.filesCount)} / ${this.formatNumber(
      server.catalogFilesTotal,
    )}`;
  }

  private load(): void {
    const seq = ++this._reqSeq;
    this.loading = true;
    this._mediaServersService.get().subscribe({
      next: (summary) => {
        if (seq !== this._reqSeq) return;
        this.summary = summary;
        this.errorMessage = null;
        this.rebuildCards();
        this.loading = false;
      },
      error: (e) => {
        if (seq !== this._reqSeq) return;
        this.errorMessage =
          e?.error?.message ?? 'Nie udało się pobrać listy serwerów multimediów.';
        this.loading = false;
      },
    });
  }

  private loadSettings(): void {
    this._settingsService.get().subscribe({
      next: (settings) => {
        this.settings = settings;
      },
      error: () => {
        // The tab is still useful without the settings group; the list load reports real outages.
        this.settings = null;
      },
    });
  }

  private rebuildCards(): void {
    const summary = this.summary;
    if (!summary) {
      this.cards = [];
      return;
    }

    this.cards = [
      {
        label: 'Obsługują ruch',
        icon: 'pi pi-bolt',
        value: `${summary.availableForTrafficCount}`,
        detail: 'serwery w rotacji przekierowań',
        highlight: true,
      },
      {
        label: 'Online',
        icon: 'pi pi-link',
        value: `${summary.onlineCount} / ${summary.totalCount}`,
        detail: 'połączonych przez websocket',
      },
      {
        label: 'Oczekujące',
        icon: 'pi pi-clock',
        value: `${summary.awaitingAcceptanceCount}`,
        detail: 'zgłoszenia do akceptacji',
      },
      {
        label: 'Pliki w kopiach',
        icon: 'pi pi-copy',
        value: this.formatNumber(summary.totalFilesMirrored),
        detail: `${this.formatMb(summary.totalStorageUsedMb)} łącznie`,
      },
      {
        label: 'Obsłużone żądania',
        icon: 'pi pi-send',
        value: this.formatNumber(summary.totalServedRequests),
        detail: `${this.formatBytes(summary.totalServedBytes)} wysłane`,
      },
    ];
  }
}
